/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * Container for Basket component
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.11.0
 * @since 1.0.0
 */

import { Fragment, useEffect, useState } from 'react';

import isEqual from 'lodash/isEqual';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../../../../assets';
import { pushGTMAnalytic } from '../../../../../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import {
    EGTMBasketCloseType,
    EGTMMBasketInteractionType,
} from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations/GTMBasketOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { makeSelectTranslationCommon } from '../../../../../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../../../../../modules/internationalization/types';
import { Loader } from '../../../../../../modules/loader/components/Loader';
import { CrossButtonStyled } from '../../../../../../modules/modal/components/styled/ModalStyled';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { Button, Checkbox, LinkBlock } from '../../../../../common';
import { switchCheckbox } from '../../../../actions/tableActions';
import { makeSelectorTableBasketCount } from '../../../../selectors/tableSelectors';
import { ISwitchCheckboxPayload } from '../../../../types/tableTypes';
import { basketGetListSuccessful } from '../basketActions';
import { makeSelectorBasketIsLoading, makeSelectorBasketList, makeSelectorInitBasketList } from '../basketSelectors';
import { IBasketGTMProps, IBasketItem } from '../basketTypes';
import BasketItemContainer from './BasketItemContainer';
import { BasketLoaderContainer } from './styled/BasketContainerStyled';
import {
    BasketCheckedAllContainer,
    BasketCheckedAllTextStyled,
    BasketContainerStyled,
    BasketFooterStyled,
    BasketTitleCounterStyled,
    BasketTitleStyled,
    BasketTitleTextStyled,
} from './styled/BasketStyled';

interface IConnectedProps {
    countBasket: number | null;
    initList: IBasketItem[];
    isLoading: boolean;
    list: IBasketItem[];
    translation: ICommonTranslation;
}

interface IDispatchProps {
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    switchCheckbox: (payload: ISwitchCheckboxPayload) => void;
    updateBasketItems: (payload: IBasketItem[]) => void;
}

export interface IBasketContainerProps {
    gtmProps?: IBasketGTMProps;
    onClose: () => void;
    title: string;
}

export type TBasketContainer = IBasketContainerProps & IDispatchProps & IConnectedProps;

export const BasketContainer = (props: TBasketContainer) => {
    const { list } = props;
    const { basket: translation } = props.translation;
    const selectAll = !!props.countBasket && props.list.every((item: IBasketItem) => item.checked);
    const countBasket = props.countBasket === null ? 0 : props.countBasket;
    const gtmUserParamsPayload: Omit<IGTMPushAnalyticPayload, 'operation'> = {
        withAccountType: true,
        withSusrId: true,
        withUserRole: true,
    };

    useEffect(() => {
        const changed = !isEqual(props.initList, props.list) && props.initList.length === props.list.length;
        if (changed !== isChanged) {
            setChanged(changed);
        }
    }, [props.list]);

    const [isChanged, setChanged] = useState(true);

    const onApply = () => {
        if (isChanged) {
            props.switchCheckbox({
                items: list.filter(item => !item.checked).map(item => ({
                    id: item.id,
                    label: item.label,
                    value: item.checked,
                })),
            });
        }
        props.onClose();
        if (props.gtmProps) props.pushGtmAnalytic({
            operation: GTMOperations.basket.onBasketPopupSubmit(props.gtmProps.sectionType),
            ...gtmUserParamsPayload,
        })
    };

    const checkedAllHandler = () => {
        props.updateBasketItems(list.map((item: IBasketItem) => ({
            ...item,
            checked: !selectAll,
        })));
        if (props.gtmProps) props.pushGtmAnalytic({
            operation: GTMOperations.basket.onBasketPopupSelectAllClick(
                props.gtmProps.sectionType,
                selectAll ? EGTMMBasketInteractionType.OFF : EGTMMBasketInteractionType.ON),
            ...gtmUserParamsPayload,
        })
    };

    const getNoChecked = () => {
        return list.filter(item => !item.checked);
    };

    const getCheckedCount = () => {
        return countBasket - getNoChecked().length;
    };

    const handleClose = (gtmCloseType: EGTMBasketCloseType) => {
        props.onClose();
        if (props.gtmProps) props.pushGtmAnalytic({
            operation: GTMOperations.basket.onBasketPopupClose(props.gtmProps.sectionType, gtmCloseType),
            ...gtmUserParamsPayload,
        })
    };

    return (
        <Fragment>
            <CrossButtonStyled
                alt="Close"
                src={icons.cancelIcon}
                onClick={() => handleClose(EGTMBasketCloseType.CROSS)}
            />

            <BasketContainerStyled
                data-component={'modal-popup'}
                data-name={'basket-container'}>
                <BasketTitleStyled>
                    <BasketTitleTextStyled>
                        {props.title}
                    </BasketTitleTextStyled>
                    <BasketTitleCounterStyled>
                        {
                            `${translation.counterFrom} ${getCheckedCount()} ` +
                            `${translation.counterTo} ${countBasket}`
                        }
                    </BasketTitleCounterStyled>
                </BasketTitleStyled>

                <BasketCheckedAllContainer>
                    <Checkbox
                        checked={selectAll}
                        className="basket__all-checkbox"
                        disabled={!countBasket}
                        onChange={checkedAllHandler}
                    />
                    <BasketCheckedAllTextStyled>
                        {translation.selectAll}
                    </BasketCheckedAllTextStyled>
                </BasketCheckedAllContainer>
                {
                    !props.isLoading ?
                        <BasketItemContainer
                            gtmProps={props.gtmProps}
                            pushGtmAnalytic={props.pushGtmAnalytic}
                            translation={translation}
                        />
                        :
                        <BasketLoaderContainer>
                            <Loader showLoader={true}/>
                        </BasketLoaderContainer>
                }

                <BasketFooterStyled>
                    <Button
                        className="basket__button-apply"
                        kind="mediumNormal"
                        onClick={onApply}
                    >
                        {translation.applyBtn}
                    </Button>
                    <LinkBlock
                        className="basket__footer-link"
                        onClick={() => handleClose(EGTMBasketCloseType.LINK)}
                    >
                        {translation.closeBtn}
                    </LinkBlock>
                </BasketFooterStyled>
            </BasketContainerStyled>
        </Fragment>
    );
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    countBasket: makeSelectorTableBasketCount(),
    initList: makeSelectorInitBasketList(),
    isLoading: makeSelectorBasketIsLoading(),
    list: makeSelectorBasketList(),
    translation: makeSelectTranslationCommon(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    pushGtmAnalytic: (payload) => dispatch(pushGTMAnalytic(payload)),
    switchCheckbox: (payload) => dispatch(switchCheckbox(payload)),
    updateBasketItems: (items) => dispatch(basketGetListSuccessful({ list: items })),
});

export default connect(mapStateToProps, mapDispatchToProps)(BasketContainer);
